import {Injectable} from '@angular/core';
import * as BasketsActions from './baskets.actions';
import {updatePurchaseOrderNumberSuccess} from './baskets.actions';
import {map} from 'rxjs/operators';
import {BASKETS_FEATURE_KEY, BasketsPartialState} from './baskets.reducer';
import {Actions, createEffect} from '@ngrx/effects';
import {DataPersistence, withCorrelationIdOf} from '@kion/ngrx-helper';
import {Basket, DeliveryMethod, Order, Payment} from '@kion/data-models';
import {BasketConnectors} from "./basket.connectors";

@Injectable()
export class BasketsEffects {
  createOrFindBasket$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.createOrFindBasket,
      {
        run: (
          action: ReturnType<typeof BasketsActions.createOrFindBasket>
        ) => {
          const basketId = action.basketId || window.localStorage.getItem('basketId');
          return this.backend.createOrFindBasket(basketId, action.userId).pipe(
            map(
              (response: Basket) => {
                window.localStorage.setItem('basketId', response.id);
                return withCorrelationIdOf(
                  action,
                  BasketsActions.createOrFindBasketSuccess({basket: response})
                )
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.createOrFindBasket>,
          error: any
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.createOrFindBasketError({error: error})
          );
        }
      }
    )
  );
  addToBasket$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.addToBasket,
      {
        run: (
          action: ReturnType<typeof BasketsActions.addToBasket>
        ) => {
          return this.backend.addToBasket(action.basketId, action.productId, action.qty, action.options, action.userId).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.addToBasketSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.addToBasket>,
          error: any
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.addToBasketError({error: error})
          );
        }
      }
    )
  );
  addToBasketWithUnit$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.addToBasketWithUnit,
      {
        run: (
          action: ReturnType<typeof BasketsActions.addToBasketWithUnit>
        ) => {
          return this.backend.addToBasketWithUnit(action.basketId, action.productId, action.qty, action.unitIso, action.options, action.userId).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.addToBasketSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.addToBasketWithUnit>,
          error: any
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.addToBasketError({error: error})
          );
        }
      }
    )
  );
  loadDeliveryDataByBasket$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.loadDeliveryDataByBasket,
      {
        run: (
          action: ReturnType<typeof BasketsActions.loadDeliveryDataByBasket>
        ) => {
          return this.backend.loadDeliveryData(action.basketId, action.userId).pipe(
            map(
              (response: DeliveryMethod[]) => withCorrelationIdOf(
                action,
                BasketsActions.loadDeliveryDataByBasketSuccess({deliveryMethods: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.loadDeliveryDataByBasket>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.loadDeliveryDataByBasketError({error: error})
          );
        }
      }
    )
  );
  setDeliveryMethod$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.setDeliveryMethod,
      {
        run: (
          action: ReturnType<typeof BasketsActions.setDeliveryMethod>
        ) => {
          return this.backend.setDeliveryData(action.basketId, action.deliveryMethod, action.userId).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.setDeliveryMethodSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.setDeliveryMethod>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.setDeliveryMethodError({error: error})
          );
        }
      }
    )
  );
  loadById$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.loadById,
      {
        run: (
          action: ReturnType<typeof BasketsActions.loadById>
        ) => {
          return this.backend.getBasketForUser(action.basketId, action.userId).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.loadByIdSuccess({basket: response})
              )
            )
          );
        },
        onError: (
          action: ReturnType<typeof BasketsActions.loadById>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.loadByIdError({error: error})
          );
        }
      }
    )
  );
  addEmailToBasket$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.addEmailToBasket,
      {
        run: (
          action: ReturnType<typeof BasketsActions.addEmailToBasket>
        ) => {
          return this.backend.addEmailToBasket(action.basketId, action.email, action.userId).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.addEmailToBasketSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.addEmailToBasket>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.addEmailToBasketError({error: error})
          );
        }
      }
    )
  );
  loadPaymentMethods$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.loadPaymentMethods,
      {
        run: (
          action: ReturnType<typeof BasketsActions.loadPaymentMethods>
        ) => {
          return this.backend.loadPaymentMethods().pipe(
            map(
              (response: Payment[]) => withCorrelationIdOf(
                action,
                BasketsActions.loadPaymentMethodsSuccess({payments: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.loadPaymentMethods>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.loadPaymentMethodsError({error: error})
          );
        }
      }
    )
  );
  loadSuportedPaymentMethods$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.loadSupportedPaymentMethods,
      {
        run: (
          action: ReturnType<typeof BasketsActions.loadSupportedPaymentMethods>
        ) => {
          return this.backend.loadSupportedPaymentMethods().pipe(
            map(
              (response: Payment[]) => withCorrelationIdOf(
                action,
                BasketsActions.loadSupportedPaymentMethodsSuccess({payments: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.loadSupportedPaymentMethods>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.loadSupportedPaymentMethodsError({error: error})
          );
        }
      }
    )
  );
  setPaymentInfo$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.setPaymentInfo,
      {
        run: (
          action: ReturnType<typeof BasketsActions.setPaymentInfo>
        ) => {
          return this.backend.setPayment(action.basketId, action.payment, action.userId).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.setPaymentInfoSuccess({basket: response})
              )
            )
          );
        },
        onError: (
          action: ReturnType<typeof BasketsActions.setPaymentInfo>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.setPaymentInfoError({error: error})
          );
        }
      }
    )
  );
  placeOrder$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.placeOrder,
      {
        run: (
          action: ReturnType<typeof BasketsActions.placeOrder>
        ) => {
          return this.backend.placeOrder(action.basketId, action.userId).pipe(
            map(
              (response: Order) => withCorrelationIdOf(
                action,
                BasketsActions.placeOrderSuccess({order: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.placeOrder>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.placeOrderError({error: error})
          );
        }
      }
    )
  );
  finalizePayment$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.finalizePayment,
      {
        run: (
          action: ReturnType<typeof BasketsActions.finalizePayment>
        ) => {
          return this.backend.finalizePayment(action.basketId, action.transactionId, action.userId).pipe(
            map(
              (order) => withCorrelationIdOf(
                action,
                BasketsActions.finalizePaymentSuccess({order: order})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.finalizePayment>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.finalizePaymentError({error: error})
          );
        }
      }
    )
  );
  changeQuantity$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.changeQuantity,
      {
        run: (
          action: ReturnType<typeof BasketsActions.changeQuantity>
        ) => {
          return this.backend.changeQuantity(action.basketId, action.entryNumber, action.quantity).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.changeQuantitySuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.changeQuantity>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.changeQuantityError({error: error})
          );
        }
      }
    )
  );
  deleteEntry$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.deleteEntry,
      {
        run: (
          action: ReturnType<typeof BasketsActions.deleteEntry>
        ) => {
          return this.backend.deleteEntry(action.basketId, action.entryNumber).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.deleteEntrySuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.deleteEntry>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.deleteEntryError({error: error})
          );
        }
      }
    )
  );
  transformAnonymousToPersonal$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.transformAnonymousToPersonal,
      {
        run: (action: ReturnType<typeof BasketsActions.transformAnonymousToPersonal>) => {
          return this.backend.transformAnonymousToPersonal(action.userId, action.oldBasketId).pipe(
            map(
              (basket: Basket) => {
                window.localStorage.setItem('basketId', basket.id);
                return withCorrelationIdOf(
                  action,
                  BasketsActions.transformAnonymousToPersonalSuccess({basket})
                );
              }
            )
          )
        },
        onError: (action: ReturnType<typeof BasketsActions.transformAnonymousToPersonal>, error) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.transformAnonymousToPersonalError({error})
          );
        }
      }
    )
  );
  addUserReference$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.addUserReference,
      {
        run: (
          action: ReturnType<typeof BasketsActions.addUserReference>
        ) => {
          return this.backend.addUserReference(action.basketId, action.userReference).pipe(
            map(
              (basket: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.addUserReferenceSuccess({basket: basket})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.addUserReference>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.addUserReferenceError({error: error})
          )
        }
      }
    )
  );
  setAddress$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.setAddress,
      {
        run: (
          action: ReturnType<typeof BasketsActions.setAddress>
        ) => {
          return this.backend.setAddress(action.addressId, action.basketId, action.isDeliveryAddress).pipe(
            map(
              response => withCorrelationIdOf(
                action,
                BasketsActions.setAddressSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.setAddress>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.setAddressError({error: error})
          );
        }
      }
    )
  );
  addPromotionCode$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.addPromotionCode,
      {
        run: (
          action: ReturnType<typeof BasketsActions.addPromotionCode>
        ) => {
          return this.backend.addPromotion(action.basketId, action.promotionCode).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.addPromotionCodeSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.addPromotionCode>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.addPromotionCodeError({error: error})
          );
        }
      }
    )
  );
  deletePromotionCode = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.deletePromotionCode,
      {
        run: (
          action: ReturnType<typeof BasketsActions.deletePromotionCode>
        ) => {
          return this.backend.deletePromotionCode(action.basketId, action.promotionCode).pipe(
            map(
              (response: Basket) => withCorrelationIdOf(
                action,
                BasketsActions.deletePromotionCodeSuccess({basket: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.deletePromotionCode>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.deletePromotionCodeError({error: error})
          );
        }
      }
    )
  );
  checkStep$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.checkStep,
      {
        run: (
          action: ReturnType<typeof BasketsActions.checkStep>,
          state: BasketsPartialState
        ) => {
          const basket = state[BASKETS_FEATURE_KEY].entities[window.localStorage.getItem('basketId')];
          let step = 'address';
          if (basket.deliveryAddress && basket.paymentAddress) {
            step = 'delivery'
          }
          if (basket.deliveryAddress && basket.paymentAddress && basket.deliveryMode) {
            step = 'payment'
          }
          if (basket.deliveryAddress && basket.paymentAddress && basket.deliveryMode && basket.paymentMode) {
            step = 'summary'
          }
          return withCorrelationIdOf(
            action,
            BasketsActions.checkStepSuccess({step: step})
          )
        },
        onError: (
          action: ReturnType<typeof BasketsActions.checkStep>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.checkStepError({error: error})
          );
        }
      }
    )
  );
  updatePurchaseOrderNumber$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.updatePurchaseOrderNumber,
      {
        run: (action: ReturnType<typeof BasketsActions.updatePurchaseOrderNumber>) => {
          return this.backend.updatePurchaseOrderNumber(action.basketId, action.purchaseOrderNumber).pipe(
            map(
              (response: Basket) => {
                return withCorrelationIdOf(
                  action,
                  updatePurchaseOrderNumberSuccess({basket: response})
                );
              }
            )
          )
        },
        onError: (action: ReturnType<typeof BasketsActions.updatePurchaseOrderNumber>, error: any) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.updatePurchaseOrderNumberError({error: error})
          );
        }
      }
    )
  )
  applyPostalCodeMapping$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.applyPostalCodeMapping,
      {
        run: (action: ReturnType<typeof BasketsActions.applyPostalCodeMapping>) => {
          return this.backend.applyPostalCodeMapping(action.basketId).pipe(
            map(
              () => {
                return withCorrelationIdOf(
                  action,
                  BasketsActions.applyPostalCodeMappingSuccess()
                );
              }
            )
          )
        },
        onError: (action: ReturnType<typeof BasketsActions.applyPostalCodeMapping>, error: any) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.applyPostalCodeMappingError({error: error})
          );
        }
      }
    )
  )
  setCompleteDelivery$ = createEffect(() =>
    this.dataPersistence.fetch(
      BasketsActions.setCompleteDelivery,
      {
        run: (action: ReturnType<typeof BasketsActions.setCompleteDelivery>) => {
          return this.backend.setCompleteDelivery(action.userId, action.basketId, action.completeDelivery).pipe(
            map(
              (response: Basket) => {
                return withCorrelationIdOf(
                  action,
                  BasketsActions.setCompleteDeliverySuccess()
                );
              }
            )
          )
        },
        onError: (action: ReturnType<typeof BasketsActions.setCompleteDelivery>, error: any) => {
          return withCorrelationIdOf(
            action,
            BasketsActions.setCompleteDeliveryError({error: error})
          );
        }
      }
    )
  )

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<BasketsPartialState>,
    private backend: BasketConnectors,
  ) {
  }
}

